/**
 * The document reducer — the single write path into a design document.
 *
 * Every mutation the editor can make is one of the actions below: drag-and-drop,
 * the inspector, keyboard shortcuts, and (later) AI-proposed edits all dispatch
 * here. That is deliberate — it means undo/redo, history coalescing and the
 * compile/preflight gate are implemented once rather than per surface, and an
 * AI edit is indistinguishable downstream from a human one
 * (docs/plans/email-editor-ai.md).
 *
 * State lives local to the editor component, not in Redux: per `docs/redux.md`,
 * models fetch and nothing else. Redux keeps the catalogs (fragments, variables,
 * translations); the document being edited is component state.
 */
import {
  Block,
  Condition,
  BlockStyle,
  DocumentSettings,
  EmailDocument,
  nextBlockId,
} from './document';
import { insertionLocationFor } from './blockFactory';
import {
  BlockLocation,
  adoptLayoutSlot,
  findBlock,
  findLocation,
  insertBlock,
  moveBlock,
  regenerateIds,
  removeBlock,
  updateBlock,
} from './tree';

/** Entries kept in each history direction. Bounds memory on long sessions. */
export const HISTORY_LIMIT = 50;

export type DocumentAction =
  | {
      type: 'addBlock';
      block: Block;
      location: BlockLocation;
      /** Select the new block after inserting. Defaults to true. */
      select?: boolean;
    }
  /**
   * Insert relative to the current selection, letting the reducer work out where.
   *
   * Exists so the palette's handler does not need the document in its closure:
   * with `addBlock` it had to compute a location, which made the callback change on
   * every keystroke and re-render every palette entry (each of which is a dnd-kit
   * draggable). Drag-and-drop still uses `addBlock`, because there the location
   * comes from measured geometry rather than from the selection.
   */
  | {
      type: 'insertBlock';
      block: Block;
      /** Which slot of a selected container to fill; see `insertionLocationFor`. */
      preferredSlot?: number | null;
    }
  | { type: 'setProps'; blockId: string; props: Record<string, unknown> }
  | { type: 'setStyle'; blockId: string; style: BlockStyle | null }
  | { type: 'setVisibility'; blockId: string; visibility: Condition | null }
  /** Which layout region a top-level block fills; null clears it. */
  | { type: 'setLayoutSlot'; blockId: string; layoutSlot: string | null }
  | { type: 'moveBlock'; blockId: string; location: BlockLocation }
  | { type: 'deleteBlock'; blockId: string }
  | { type: 'duplicateBlock'; blockId: string }
  | { type: 'setSettings'; settings: Partial<DocumentSettings> }
  | {
      type: 'setLayout';
      layoutKey: string | null;
      layoutSlot?: string;
      /**
       * Applied without counting as a user edit.
       *
       * The editor syncs a *contained* document's layout from the template's own
       * `layout_key` once the prop resolves. That is not an edit, and counting it as
       * one would bump `revision` and therefore write an envelope into a legacy
       * template merely by opening it.
       */
      silent?: boolean;
    }
  /** The body was hand-edited (or a divergence was detected on load). */
  | { type: 'setDetached'; detached: boolean }
  /** Wholesale replacement — loading a template, or restoring from history. */
  | { type: 'replaceDocument'; document: EmailDocument; resetHistory?: boolean };

export type EditorAction =
  | (DocumentAction & {
      /**
       * Identifies the *edit run* this action belongs to, e.g.
       * `"b3ofce:text"` for typing in one block's one field. Consecutive actions
       * carrying the same key collapse into a single undo step; a different key,
       * an unkeyed action, `endEditRun`, a selection change or an undo/redo all
       * end the run.
       *
       * It is a key rather than a boolean because the caller cannot reliably
       * know whether it is mid-run — and guessing wrong is destructive, not
       * cosmetic: a "merge into the previous entry" on the *first* keystroke
       * merges into whatever unrelated action came before, so one ⌘Z then
       * discards that too. (Observed: typing into a freshly inserted block made
       * one undo delete the block.) The reducer owns the decision because only
       * it knows what the previous entry was.
       */
      coalesce?: string;
    })
  | { type: 'selectBlock'; blockId: string | null }
  /**
   * The output changed without the document changing.
   *
   * Only the detached case: the hand-edited body lives outside the document (see
   * `EmailDocument.detached`), so a keystroke in the source panel has to move
   * `revision` — that is what write-back keys on — while leaving the document's
   * snapshot and the undo history alone. Source-level undo is the code editor's own.
   */
  | { type: 'touch' }
  /** Ends the current run, so the next keystroke starts a new undo step. */
  | { type: 'endEditRun' }
  | { type: 'undo' }
  | { type: 'redo' };

export interface EditorState {
  document: EmailDocument;
  selectedBlockId: string | null;
  past: EmailDocument[];
  future: EmailDocument[];
  /** The run the last history push belonged to; null when no run is open. */
  coalesceKey: string | null;
  /**
   * Counts document changes the **user** made — every action except
   * `replaceDocument`, which is how a template is *loaded*.
   *
   * This is what write-back keys on, and the distinction is the whole point: the
   * compiled body also changes when the Redux fragment catalog arrives after mount
   * or when a registry rebuild alters an emitter, and emitting then would mark a
   * template dirty (and, for a legacy body, rewrite it) with no user action at all.
   * Undo and redo *do* bump it — reverting an edit is a change that has to be
   * written back.
   */
  revision: number;
}

export function createEditorState(document: EmailDocument): EditorState {
  return {
    document,
    selectedBlockId: null,
    past: [],
    future: [],
    coalesceKey: null,
    revision: 0,
  };
}

export function canUndo(state: EditorState): boolean {
  return state.past.length > 0;
}

export function canRedo(state: EditorState): boolean {
  return state.future.length > 0;
}

/**
 * A block just added at the root takes the layout region of its neighbours.
 *
 * The same rule `moveBlock` applies — dragging between the layout's bands is how a
 * region is chosen — and inserts have to follow it or they land in no declared
 * region at all. A fresh block carries no `layoutSlot`, `compileBody` falls back to
 * `DEFAULT_LAYOUT_SLOT` ('content'), and a layout that declares its own block names
 * (`layout/xxxl/base` has header/intro_content/footer and no 'content') then fails
 * preflight with `layout-slot-not-declared`. The email cannot be saved, and because
 * the block compiled into a discarded region it never rendered, so it could not be
 * dragged into a band either — a detached master was impossible to edit at all.
 * Found in the browser.
 *
 * Nested inserts are untouched: a region belongs to a top-level block, and the
 * container's own slot already covers its children.
 */
function settleLayoutSlot(
  document: EmailDocument,
  blocks: Block[],
  location: BlockLocation,
  blockId: string,
): Block[] {
  if (location.parentId !== null) return blocks;
  if (!document.layoutKey || document.rawPassthrough) return blocks;
  return adoptLayoutSlot(blocks, blockId);
}

/** Applies a document action, returning the same object when nothing changed. */
function applyDocumentAction(
  document: EmailDocument,
  action: DocumentAction,
  selectedBlockId: string | null,
): EmailDocument {
  switch (action.type) {
    case 'replaceDocument':
      return action.document;

    case 'addBlock': {
      const blocks = insertBlock(document.blocks, action.block, action.location);
      if (blocks === document.blocks) return document;
      return {
        ...document,
        blocks: settleLayoutSlot(document, blocks, action.location, action.block.id),
      };
    }

    case 'insertBlock': {
      const location = insertionLocationFor(
        document,
        selectedBlockId,
        action.preferredSlot,
      );
      const blocks = insertBlock(document.blocks, action.block, location);
      if (blocks === document.blocks) return document;
      return {
        ...document,
        blocks: settleLayoutSlot(document, blocks, location, action.block.id),
      };
    }

    case 'setProps': {
      const blocks = updateBlock(document.blocks, action.blockId, (block) => ({
        ...block,
        // Partial merge: the inspector edits one control at a time, and an AI
        // `set_props` should not have to restate every prop to change one.
        props: { ...block.props, ...action.props },
      }));
      return blocks === document.blocks ? document : { ...document, blocks };
    }

    case 'setStyle': {
      const blocks = updateBlock(document.blocks, action.blockId, (block) => {
        const next: Block = { ...block };
        if (action.style === null) delete next.style;
        else next.style = action.style;
        return next;
      });
      return blocks === document.blocks ? document : { ...document, blocks };
    }

    case 'setVisibility': {
      const blocks = updateBlock(document.blocks, action.blockId, (block) => {
        const next: Block = { ...block };
        if (action.visibility === null) delete next.visibility;
        else next.visibility = action.visibility;
        return next;
      });
      return blocks === document.blocks ? document : { ...document, blocks };
    }

    case 'setLayoutSlot': {
      const blocks = updateBlock(document.blocks, action.blockId, (block) => {
        const next: Block = { ...block };
        if (action.layoutSlot === null) delete next.layoutSlot;
        else next.layoutSlot = action.layoutSlot;
        return next;
      });
      return blocks === document.blocks ? document : { ...document, blocks };
    }

    case 'moveBlock': {
      const moved = moveBlock(document.blocks, action.blockId, action.location);
      if (moved === document.blocks) return document;

      // Dragging between the layout's bands *is* how a region is chosen; see
      // `adoptLayoutSlot`. Only at the root, and only when there are regions to be in.
      const blocks =
        action.location.parentId === null && document.layoutKey && !document.rawPassthrough
          ? adoptLayoutSlot(moved, action.blockId)
          : moved;

      return { ...document, blocks };
    }

    case 'deleteBlock': {
      const { blocks, removed } = removeBlock(document.blocks, action.blockId);
      return removed ? { ...document, blocks } : document;
    }

    case 'duplicateBlock': {
      const location = findLocation(document.blocks, action.blockId);
      const source = findBlock(document.blocks, action.blockId);
      if (!location || !source) return document;

      // Fresh ids throughout: ids surface as `data-cvn-id`, and a duplicate id
      // makes canvas hit-testing ambiguous. The copy lands directly after the
      // original, in the same slot.
      const blocks = insertBlock(document.blocks, regenerateIds(source), {
        ...location,
        index: location.index + 1,
      });
      return { ...document, blocks };
    }

    case 'setSettings':
      return {
        ...document,
        settings: { ...document.settings, ...action.settings },
      };

    case 'setDetached': {
      if ((document.detached ?? false) === action.detached) return document;
      const next: EmailDocument = { ...document };
      if (action.detached) next.detached = true;
      else delete next.detached;
      return next;
    }

    case 'setLayout': {
      if (
        document.layoutKey === action.layoutKey &&
        (action.layoutSlot === undefined ||
          document.layoutSlot === action.layoutSlot)
      ) {
        return document;
      }
      const next: EmailDocument = { ...document, layoutKey: action.layoutKey };
      if (action.layoutSlot !== undefined) next.layoutSlot = action.layoutSlot;
      return next;
    }

    default:
      return document;
  }
}

/** The id a document action should leave selected, or undefined to leave as-is. */
function nextSelection(
  state: EditorState,
  action: DocumentAction,
  document: EmailDocument,
): string | null | undefined {
  switch (action.type) {
    case 'addBlock':
      return action.select === false ? undefined : action.block.id;

    case 'insertBlock':
      return action.block.id;

    case 'deleteBlock':
      // Selection must not survive its block — a stale id would leave the
      // inspector bound to something no longer in the tree.
      return state.selectedBlockId === action.blockId
        ? null
        : undefined;

    case 'replaceDocument':
      return state.selectedBlockId &&
        findLocation(document.blocks, state.selectedBlockId)
        ? undefined
        : null;

    default:
      return undefined;
  }
}

function pushHistory(
  past: EmailDocument[],
  document: EmailDocument,
): EmailDocument[] {
  const next = [...past, document];
  return next.length > HISTORY_LIMIT
    ? next.slice(next.length - HISTORY_LIMIT)
    : next;
}

export function editorReducer(
  state: EditorState,
  action: EditorAction,
): EditorState {
  switch (action.type) {
    // Selecting elsewhere ends the run: coming back to the same field later must
    // start a new undo step, not extend the earlier one.
    case 'selectBlock':
      return state.selectedBlockId === action.blockId && state.coalesceKey === null
        ? state
        : { ...state, selectedBlockId: action.blockId, coalesceKey: null };

    case 'endEditRun':
      return state.coalesceKey === null ? state : { ...state, coalesceKey: null };

    case 'touch':
      return { ...state, revision: state.revision + 1 };

    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        document: previous,
        past: state.past.slice(0, -1),
        future: [state.document, ...state.future].slice(0, HISTORY_LIMIT),
        coalesceKey: null,
        revision: state.revision + 1,
        selectedBlockId:
          state.selectedBlockId &&
          findLocation(previous.blocks, state.selectedBlockId)
            ? state.selectedBlockId
            : null,
      };
    }

    case 'redo': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        document: next,
        past: pushHistory(state.past, state.document),
        future: rest,
        coalesceKey: null,
        revision: state.revision + 1,
        selectedBlockId:
          state.selectedBlockId &&
          findLocation(next.blocks, state.selectedBlockId)
            ? state.selectedBlockId
            : null,
      };
    }

    default: {
      const documentAction = action as DocumentAction & { coalesce?: string };
      const document = applyDocumentAction(
        state.document,
        documentAction,
        state.selectedBlockId,
      );

      // A no-op action must not consume a history slot, or undo appears broken:
      // the user presses Cmd+Z and nothing visible happens.
      if (document === state.document) {
        const selection = nextSelection(state, documentAction, document);
        return selection === undefined || selection === state.selectedBlockId
          ? state
          : { ...state, selectedBlockId: selection };
      }

      const resetHistory =
        documentAction.type === 'replaceDocument' &&
        documentAction.resetHistory === true;

      const selection = nextSelection(state, documentAction, document);

      /**
       * Continuation of an open run — same key as the last push — so the entry
       * already holding this run's pre-edit state stays and the intermediate is
       * dropped. The `past.length > 0` guard is what makes the *first* action of
       * a run push even though it carries a key.
       */
      const runKey = documentAction.coalesce ?? null;
      const continues =
        runKey !== null && state.coalesceKey === runKey && state.past.length > 0;

      return {
        document,
        // Any new edit invalidates the redo branch.
        past: resetHistory
          ? []
          : continues
            ? state.past
            : pushHistory(state.past, state.document),
        future: [],
        coalesceKey: resetHistory ? null : runKey,
        // Loading a template is not an edit: `replaceDocument` must not make the
        // form dirty, or opening one would offer to save it back.
        // Loading a template is not an edit, and neither is a silent sync — both
        // would otherwise mark a template dirty without the user touching it.
        revision:
          documentAction.type === 'replaceDocument' ||
          (documentAction.type === 'setLayout' && documentAction.silent === true)
            ? state.revision
            : state.revision + 1,
        selectedBlockId:
          selection === undefined ? state.selectedBlockId : selection,
      };
    }
  }
}

/** Convenience for the palette: a fresh block of `type` with given props. */
export function createBlock(
  type: string,
  props: Record<string, unknown> = {},
): Block {
  return { id: nextBlockId(), type, props };
}
